import { type ComponentProps, memo, type ReactElement, useMemo, useState } from 'react';
import { type BackgroundProps } from 'react-imgix';

import { type Clip, ClipStatus } from '@air/api/types';
import { ImgixImage } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { AssetGalleryCardFailedTranscode } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardFailedTranscode';
import { AssetGalleryCardProcessing } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardProcessing';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { getRoundedImgixDimensions } from '~/utils/getRoundedImgixDimensions';
import { isPreviewSrc } from '~/utils/isPreviewSrc';

type AssetGalleryCardThumbnailImageProps = {
  /** URI for the image to be displayed in the clip thumbnail */
  src: string | undefined;
  /** URI for the animated image to be displayed on hover for a clip e.g an animated preview on video hover */
  animatedSrc?: string;
  /** width of the image that should be rendered in the gallery */
  width: number;
  /** height of the image that should be rendered in the gallery */
  height: number;
  /**
   * Imigix parameters
   */
  imgixParams?: BackgroundProps['imgixParams'];
  /** Called when the final CDN thumbnail image has loaded */
  onFinalImageLoad?: () => void;
} & Pick<Clip, 'status' | 'ext' | 'description'> &
  Pick<ComponentProps<'img'>, 'className'>;

export const AssetGalleryCardThumbnailImage = memo(
  ({
    animatedSrc,
    className,
    description,
    ext,
    height,
    imgixParams,
    onFinalImageLoad,
    status,
    src,
    width,
  }: AssetGalleryCardThumbnailImageProps) => {
    const [isLoaded, setLoaded] = useState(false);

    const imgixDimensions = useMemo(() => getRoundedImgixDimensions({ width, height }), [height, width]);

    if ([ClipStatus.failed, ClipStatus.nonTranscodable].includes(status)) {
      return <AssetGalleryCardFailedTranscode ext={ext} />;
    }

    if (!src) {
      return <AssetGalleryCardProcessing ext={ext} />;
    }

    const isPreviewSource = isPreviewSrc(src);

    const imageClassNames: ComponentProps<'img'>['className'] = cn(
      'block min-h-full w-full rounded-4 object-cover',
      isLoaded ? 'opacity-100' : 'opacity-0',
      className,
    );

    let image: ReactElement;

    if (animatedSrc) {
      // biome-ignore lint/performance/noImgElement: animatedSrc is a video preview URL, not eligible for Next.js Image optimization
      image = <img alt={description} src={animatedSrc} className={imageClassNames} />;
    } else if (isPreviewSource) {
      // No opacity transition — the shimmer handles the loading state, image renders at full opacity immediately
      image = (
        // biome-ignore lint/performance/noImgElement: preview src is a blob/data/edits-bucket URL, not eligible for Next.js Image optimization
        <img
          alt={description || ''}
          src={src}
          className={cn('block min-h-full w-full rounded-4 object-cover', className)}
        />
      );
    } else {
      image = (
        <ImgixImage
          alt={description || ''}
          fallbackUrl={getCloudFrontUrlFromImgixUrl(src)}
          src={src}
          imgixParams={{
            fit: 'crop',
            cs: 'origin',
            ...imgixParams,
          }}
          width={imgixDimensions.width}
          height={imgixDimensions.height}
          onLoad={() => {
            setLoaded(true);
            onFinalImageLoad?.();
          }}
          className={imageClassNames}
        />
      );
    }

    if (isPreviewSource) {
      return (
        <div className="relative h-full w-full">
          {image}
          <div className={cn('shimmer-background absolute inset-0 rounded-4 opacity-40')} />
        </div>
      );
    }

    return image;
  },
);

AssetGalleryCardThumbnailImage.displayName = 'AssetGalleryCardThumbnailImage';
